import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config.js';
import LeadTimeSelector from '../components/LeadTimeSelector.jsx';
import { apiErrorMessage } from '../utils/apiError';
import { fmtMoney, fmtPct, asOf } from '../utils/priceMath';
import { entryLineFinancials } from '../utils/salesOrderMath';

const API_BASE = config.API_BASE_URL;

const Notification = ({ message, type, onDismiss }) => {
    if (!message) return null;
    return (
        <div
            className={`alert alert-${type} alert-dismissible fade show`}
            role="alert"
            style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1050, minWidth: '300px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
        >
            {message}
            <button type="button" className="btn-close" onClick={onDismiss} aria-label="Close"></button>
        </div>
    );
};

const SearchablePartDropdown = ({ parts, onPartSelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredParts = useMemo(() => {
        const q = searchTerm.toLowerCase();
        const matches = searchTerm.length < 1
            ? parts
            : parts.filter(p => (p.product_id?.toLowerCase().includes(q)) || (p.description?.toLowerCase().includes(q)));
        const uniqueMap = new Map();
        matches.forEach(p => {
            if (!uniqueMap.has(p.product_id) || p.is_current_revision) {
                uniqueMap.set(p.product_id, p);
            }
        });
        return Array.from(uniqueMap.values());
    }, [parts, searchTerm]);

    const handleSelect = (partId) => {
        onPartSelect(partId);
        setSearchTerm('');
        setIsOpen(false);
    };

    return (
        <div className="dropdown" style={{ position: 'relative' }}>
            <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search for an existing part..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsOpen(true)}
                onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            />
            {isOpen && (
                <ul className="dropdown-menu show" style={{ maxHeight: '250px', overflowY: 'auto', width: '100%', zIndex: 1050 }}>
                    {filteredParts.length > 0 ? (
                        filteredParts.map(p => (
                            <li key={p.part_id}>
                                <a
                                    className="dropdown-item"
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); handleSelect(p.part_id); }}
                                >
                                    <strong>{p.product_id}</strong>
                                    {p.revision_code ? <span className="text-muted"> (Rev {p.revision_code})</span> : null}
                                    : {p.description}
                                </a>
                            </li>
                        ))
                    ) : (
                        <li><span className="dropdown-item-text text-muted">No parts found</span></li>
                    )}
                </ul>
            )}
        </div>
    );
};

const ContactSelect = ({ label, contacts, value, onChange, disabled }) => (
    <div>
        <label className="form-label small text-muted mb-1">{label}</label>
        <select
            className="form-select form-select-sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
        >
            <option value="">-- None --</option>
            {contacts.map(c => (
                <option key={c.contact_id} value={c.contact_id}>
                    {c.name}{c.role ? ` (${c.role})` : ''}
                </option>
            ))}
        </select>
    </div>
);

export default function SalesOrderEntryPage() {
    const navigate = useNavigate();
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [saving, setSaving] = useState(false);

    const [customers, setCustomers] = useState([]);
    const [customerParts, setCustomerParts] = useState([]);
    const [contacts, setContacts] = useState([]);

    const [customerId, setCustomerId] = useState('');
    const [customerPO, setCustomerPO] = useState('');
    // Required at SO create. Pre-fills from customer's default_fulfillment_type
    // when a customer is picked (via the customer-load effect below). Operator
    // must consciously confirm/override before save (validate() blocks empty).
    const [fulfillmentType, setFulfillmentType] = useState('');
    const [orderContactId, setOrderContactId] = useState('');
    const [billingContactId, setBillingContactId] = useState('');
    const [shippingContactId, setShippingContactId] = useState('');
    const [addresses, setAddresses] = useState([]);
    const [shippingAddressId, setShippingAddressId] = useState('');

    const [lines, setLines] = useState([{ key: 1, part_id: null, description: '', quantity: 1, unit_price: 0 }]);
    const [lineKeyCounter, setLineKeyCounter] = useState(2);

    const [completionMode, setCompletionMode] = useState('lead_time');
    const [leadTime, setLeadTime] = useState('Standard (10-14 Days)');
    const [promisedDate, setPromisedDate] = useState('');

    const [shipping, setShipping] = useState(0);
    const [additionalFees, setAdditionalFees] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [tax, setTax] = useState(0);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/customers`);
                if (res.ok) setCustomers(await res.json());
            } catch (err) {
                setNotification({ message: 'Failed to load customers', type: 'danger' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!customerId) {
            setCustomerParts([]);
            setContacts([]);
            setOrderContactId(''); setBillingContactId(''); setShippingContactId('');
            setAddresses([]); setShippingAddressId('');
            return;
        }
        (async () => {
            try {
                const [partsRes, contactsRes, custRes, addrRes] = await Promise.all([
                    fetch(`${API_BASE}/customers/${customerId}/parts`),
                    fetch(`${API_BASE}/customers/${customerId}/contacts`),
                    fetch(`${API_BASE}/customers/${customerId}`),
                    fetch(`${API_BASE}/customers/${customerId}/addresses`),
                ]);
                setCustomerParts(partsRes.ok ? await partsRes.json() : []);
                const contactList = contactsRes.ok ? await contactsRes.json() : [];
                setContacts(contactList);
                // Default the three contacts to the first contact so the SO isn't blank
                if (contactList.length > 0) {
                    const firstId = String(contactList[0].contact_id);
                    setOrderContactId(firstId);
                    setBillingContactId(firstId);
                    setShippingContactId(firstId);
                }
                // Pre-fill fulfillment_type from the customer's default
                if (custRes.ok) {
                    const cust = await custRes.json();
                    setFulfillmentType(cust.default_fulfillment_type || 'shipping');
                }
                // Load ship-to addresses + pre-select the customer's default
                const addrList = addrRes.ok ? await addrRes.json() : [];
                setAddresses(addrList);
                const defAddr = addrList.find(a => a.is_default) || addrList[0];
                setShippingAddressId(defAddr ? String(defAddr.address_id) : '');
            } catch (err) {
                setNotification({ message: 'Failed to load customer data', type: 'danger' });
            }
        })();
    }, [customerId]);

    // Per-part stored cost (parts.cost_labor_material) so each typed price can
    // be judged against a cost and flagged when it is below it (U-S #5 / F45).
    // /customers/{id}/parts returns only price, so GET /parts/{id} is fetched
    // once per distinct part. Map: part_id -> { cost: number|null, as_of }.
    // A part absent from the map is still loading; cost null = no stored cost.
    const [partCosts, setPartCosts] = useState({});

    const loadPartCost = async (partId) => {
        if (partId == null || Object.prototype.hasOwnProperty.call(partCosts, partId)) return;
        try {
            const res = await fetch(`${API_BASE}/parts/${partId}`);
            const data = res.ok ? await res.json() : null;
            const raw = data ? data.cost_labor_material : null;
            const cost = raw != null && Number.isFinite(parseFloat(raw)) ? parseFloat(raw) : null;
            setPartCosts(prev => ({
                ...prev,
                [partId]: { cost, as_of: data?.cost_updated_at || data?.updated_at || null },
            }));
        } catch (err) {
            setPartCosts(prev => ({ ...prev, [partId]: { cost: null, as_of: null } }));
        }
    };

    const addPartLine = (partId) => {
        const part = customerParts.find(p => p.part_id === partId);
        if (!part) return;
        const price = parseFloat(part.price) || 0;
        setLines(prev => [
            ...prev,
            {
                key: lineKeyCounter,
                part_id: part.part_id,
                product_id: part.product_id,
                description: part.description || '',
                quantity: 1,
                unit_price: price,
            }
        ]);
        setLineKeyCounter(k => k + 1);
        loadPartCost(part.part_id);
    };

    const updateLine = (key, field, value) => {
        setLines(prev => prev.map(l => l.key === key ? { ...l, [field]: value } : l));
    };

    const removeLine = (key) => {
        setLines(prev => prev.filter(l => l.key !== key));
    };

    const realLines = useMemo(
        () => lines.filter(l => l.part_id != null && Number(l.quantity) > 0),
        [lines]
    );
    const subtotal = useMemo(
        () => realLines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0),
        [realLines]
    );
    const grandTotal = subtotal + (Number(shipping) || 0) + (Number(additionalFees) || 0) + (Number(tax) || 0) - (Number(discount) || 0);

    // Each real line with its stored unit cost attached (undefined while the
    // /parts/{id} fetch is in flight) and the per-line math from
    // entryLineFinancials(): ext = qty × price, margin = (price − cost) / price.
    const costedLines = useMemo(
        () => realLines.map(l => {
            const entry = Object.prototype.hasOwnProperty.call(partCosts, l.part_id) ? partCosts[l.part_id] : undefined;
            const withCost = entry === undefined ? l : { ...l, unit_cost: entry.cost };
            return { ...withCost, cost_as_of: entry?.as_of || null, fin: entryLineFinancials(withCost) };
        }),
        [realLines, partCosts]
    );
    const costTotals = useMemo(() => {
        const known = costedLines.filter(l => l.fin.ext_cost != null);
        const cost = known.length ? known.reduce((s, l) => s + l.fin.ext_cost, 0) : null;
        return {
            cost,
            costed: known.length,
            total: costedLines.length,
            margin: cost != null && subtotal > 0 ? (subtotal - cost) / subtotal : null,
        };
    }, [costedLines, subtotal]);
    const belowCostLines = useMemo(() => costedLines.filter(l => l.fin.below_cost), [costedLines]);

    const validate = () => {
        if (!customerId) return 'Select a customer.';
        if (!fulfillmentType) return 'Fulfillment is required — pick Carrier Shipment, Customer Pickup, or We Deliver.';
        if (realLines.length === 0) return 'Add at least one line item with part and quantity.';
        for (const l of realLines) {
            if (!l.description || !l.description.trim()) return 'Every line needs a description.';
            if (!(Number(l.unit_price) >= 0)) return 'Unit price must be a non-negative number.';
        }
        if (completionMode === 'date' && !promisedDate) return 'Promised date is required when mode is Date.';
        return null;
    };

    const handleSave = async () => {
        const err = validate();
        if (err) {
            setNotification({ message: err, type: 'warning' });
            return;
        }
        // Warn (do not block) when a typed price is below the part's stored cost.
        if (belowCostLines.length > 0) {
            const list = belowCostLines
                .map(l => `${l.product_id}: ${fmtMoney(l.fin.unit_price)} < cost ${fmtMoney(l.fin.unit_cost)}`)
                .join('\n');
            const ok = window.confirm(
                `${belowCostLines.length} line(s) are priced below the part's stored cost:\n\n${list}\n\nSave anyway?`
            );
            if (!ok) return;
        }
        setSaving(true);
        try {
            const payload = {
                customer_id: parseInt(customerId, 10),
                customer_po: customerPO || null,
                fulfillment_type: fulfillmentType || 'shipping',
                notes: notes || null,
                order_contact_id: orderContactId ? parseInt(orderContactId, 10) : null,
                billing_contact_id: billingContactId ? parseInt(billingContactId, 10) : null,
                shipping_contact_id: shippingContactId ? parseInt(shippingContactId, 10) : null,
                shipping_address_id: shippingAddressId ? parseInt(shippingAddressId, 10) : null,
                shipping_amount: Number(shipping) || 0,
                additional_fees: Number(additionalFees) || 0,
                tax_amount: Number(tax) || 0,
                discount_amount: Number(discount) || 0,
                lead_time: completionMode === 'lead_time' ? leadTime : null,
                promised_date: completionMode === 'date' ? promisedDate : null,
                lines: realLines.map(l => ({
                    part_id: l.part_id,
                    description: l.description,
                    quantity_ordered: parseInt(l.quantity, 10),
                    unit_price: Number(l.unit_price) || 0,
                })),
            };
            const res = await fetch(`${API_BASE}/sales_orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw new Error(await apiErrorMessage(res, "Failed to create sales order"));
            }
            const data = await res.json();
            setNotification({ message: `Created ${data.order_number}`, type: 'success' });
            setTimeout(() => navigate('/sales-orders'), 800);
        } catch (e) {
            setNotification({ message: `Error: ${e.message}`, type: 'danger' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="container-fluid p-3" style={{ maxWidth: '1200px' }}>
            <Notification {...notification} onDismiss={() => setNotification({ message: '', type: '' })} />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0 fw-bold">Create Sales Order</h4>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/sales-orders')} disabled={saving}>
                        Cancel
                    </button>
                    <button className="btn btn-success btn-sm" onClick={handleSave} disabled={saving}>
                        {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : 'Save Sales Order'}
                    </button>
                </div>
            </div>

            <div className="card card-body">
                <h5 className="mt-1">1. Customer &amp; Contacts</h5>
                <div className="row g-2 mb-3">
                    <div className="col-md-4">
                        <label className="form-label small text-muted mb-1">Customer</label>
                        <select
                            className="form-select form-select-sm"
                            value={customerId}
                            onChange={e => setCustomerId(e.target.value)}
                        >
                            <option value="">-- Select Customer --</option>
                            {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Customer PO #</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={customerPO}
                            onChange={e => setCustomerPO(e.target.value)}
                            placeholder="PO-12345"
                        />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1" title="Required — defaults from customer's preference. Pickup orders use Mark Ready for Pickup → Confirm Pickup workflow on the SO.">
                            Fulfillment <span className="text-danger">*</span>
                        </label>
                        <select
                            className={`form-select form-select-sm ${customerId && !fulfillmentType ? 'border-danger' : ''}`}
                            value={fulfillmentType}
                            onChange={e => setFulfillmentType(e.target.value)}
                            disabled={!customerId}
                        >
                            <option value="">— Select —</option>
                            <option value="shipping">Carrier Shipment</option>
                            <option value="pickup">Customer Pickup</option>
                            <option value="delivery">We Deliver</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <ContactSelect label="Order Contact" contacts={contacts} value={orderContactId} onChange={setOrderContactId} disabled={!customerId} />
                    </div>
                    <div className="col-md-2">
                        <ContactSelect label="Billing Contact" contacts={contacts} value={billingContactId} onChange={setBillingContactId} disabled={!customerId} />
                    </div>
                    <div className="col-md-2">
                        <ContactSelect label="Shipping Contact" contacts={contacts} value={shippingContactId} onChange={setShippingContactId} disabled={!customerId} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1" title="Where this order ships. Manage a customer's locations on the Customers page.">Ship To</label>
                        <select
                            className="form-select form-select-sm"
                            value={shippingAddressId}
                            onChange={e => setShippingAddressId(e.target.value)}
                            disabled={!customerId}
                        >
                            {addresses.length === 0 && <option value="">(no saved addresses)</option>}
                            {addresses.map(a => (
                                <option key={a.address_id} value={a.address_id}>
                                    {a.label}{a.city ? ` — ${a.city}, ${a.state || ''}` : ''}{a.is_default ? ' (default)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <h5 className="mt-2">2. Line Items</h5>
                <div className="mb-2">
                    <label className="form-label small text-muted mb-1">Add Part</label>
                    {customerId && customerParts.length > 0 ? (
                        <SearchablePartDropdown parts={customerParts} onPartSelect={addPartLine} />
                    ) : (
                        <div className="text-muted small">
                            {customerId ? 'This customer has no parts in the catalog.' : 'Select a customer to load parts.'}
                        </div>
                    )}
                </div>

                <div className="table-responsive mb-3">
                    <table className="table table-sm align-middle">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '15%' }}>Product ID</th>
                                <th>Description</th>
                                <th style={{ width: '100px' }}>Qty</th>
                                <th style={{ width: '120px' }} title="Book price (parts.price) pre-filled; type to override. This is what the SO bills.">Unit Price</th>
                                <th style={{ width: '110px' }} title="Stored part cost (parts.cost_labor_material = labor + gas + consumables + material + coating), per unit — not a quote-engine cost">Est Unit Cost</th>
                                <th style={{ width: '90px' }} title="(unit price − est unit cost) ÷ unit price">Margin</th>
                                <th style={{ width: '110px' }} title="qty × unit price">Line Total</th>
                                <th style={{ width: '40px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {costedLines.length === 0 ? (
                                <tr><td colSpan="8" className="text-muted small">No line items yet. Pick a part above to add one.</td></tr>
                            ) : costedLines.map(l => (
                                <tr key={l.key} className={l.fin.below_cost ? 'table-danger' : ''}>
                                    <td className="small">
                                        <strong>{l.product_id || ''}</strong>
                                        {l.fin.below_cost && (
                                            <span
                                                className="badge bg-danger ms-2"
                                                style={{ fontSize: '0.65rem' }}
                                                title={`unit price ${fmtMoney(l.fin.unit_price)} is below the stored cost ${fmtMoney(l.fin.unit_cost)}`}
                                            >
                                                below cost
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={l.description}
                                            onChange={e => updateLine(l.key, 'description', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            min="1"
                                            className="form-control form-control-sm"
                                            value={l.quantity}
                                            onChange={e => updateLine(l.key, 'quantity', parseInt(e.target.value, 10) || 0)}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            className="form-control form-control-sm"
                                            value={l.unit_price}
                                            onChange={e => updateLine(l.key, 'unit_price', parseFloat(e.target.value) || 0)}
                                        />
                                    </td>
                                    <td
                                        className="small"
                                        title={l.fin.cost_loading
                                            ? 'loading parts.cost_labor_material…'
                                            : (l.fin.unit_cost == null
                                                ? 'no stored cost on this part'
                                                : `parts.cost_labor_material${l.cost_as_of ? ` (stored ${asOf(l.cost_as_of).replace('as of ', '')})` : ''}`)}
                                    >
                                        {l.fin.cost_loading
                                            ? <span className="text-muted">…</span>
                                            : fmtMoney(l.fin.unit_cost, { dash: true })}
                                        {!l.fin.cost_loading && l.fin.unit_cost == null && (
                                            <div className="text-muted" style={{ fontSize: '0.65rem' }}>no cost</div>
                                        )}
                                    </td>
                                    <td
                                        className="small"
                                        title={l.fin.margin == null
                                            ? 'needs a unit cost and a unit price > 0'
                                            : `(${fmtMoney(l.fin.unit_price)} − ${fmtMoney(l.fin.unit_cost)}) ÷ ${fmtMoney(l.fin.unit_price)}`}
                                    >
                                        <span className={l.fin.margin == null ? 'text-muted' : l.fin.margin < 0 ? 'text-danger fw-bold' : l.fin.margin < 0.15 ? 'text-warning-emphasis' : 'text-success'}>
                                            {fmtPct(l.fin.margin)}
                                        </span>
                                    </td>
                                    <td className="small" title={`${l.fin.qty} × ${fmtMoney(l.fin.unit_price)}`}>{fmtMoney(l.fin.ext_price)}</td>
                                    <td>
                                        <button
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => removeLine(l.key)}
                                            title="Remove line"
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <h5 className="mt-2">3. Delivery / Completion</h5>
                <div className="p-2 border rounded mb-3 bg-light" style={{ fontSize: '0.875rem' }}>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                            <div className="form-check form-check-inline m-0">
                                <input className="form-check-input" type="radio" name="completionMode" id="modeLeadTime" value="lead_time" checked={completionMode === 'lead_time'} onChange={() => setCompletionMode('lead_time')} />
                                <label className="form-check-label small" htmlFor="modeLeadTime">Lead Time</label>
                            </div>
                            <div className="form-check form-check-inline m-0">
                                <input className="form-check-input" type="radio" name="completionMode" id="modePromisedDate" value="date" checked={completionMode === 'date'} onChange={() => setCompletionMode('date')} />
                                <label className="form-check-label small" htmlFor="modePromisedDate">Date</label>
                            </div>
                        </div>
                        {completionMode === 'lead_time' ? (
                            <LeadTimeSelector value={leadTime} onChange={setLeadTime} />
                        ) : (
                            <input type="date" className="form-control form-control-sm" style={{ maxWidth: '180px' }} value={promisedDate} onChange={(e) => setPromisedDate(e.target.value)} />
                        )}
                    </div>
                </div>

                <div className="row g-2 mb-2">
                    <div className="col-md-8">
                        <h5 className="mb-1">4. Totals</h5>
                        <div className="d-flex align-items-end gap-2 p-2 border rounded bg-light flex-wrap" style={{ fontSize: '0.85rem' }}>
                            <div style={{ width: '100px' }}>
                                <label className="form-label small mb-0" title="Σ qty × unit price">Subtotal</label>
                                <input type="text" className="form-control form-control-sm" value={fmtMoney(subtotal)} readOnly />
                            </div>
                            <div style={{ minWidth: '170px' }} title="Σ qty × stored part cost over the lines that have a cost; margin = (subtotal − cost) ÷ subtotal">
                                <label className="form-label small mb-0">Est cost · margin</label>
                                <div className="form-control form-control-sm bg-white" style={{ fontSize: '0.8rem' }}>
                                    {fmtMoney(costTotals.cost, { dash: true })}
                                    {' · '}
                                    <span className={costTotals.margin != null && costTotals.margin < 0 ? 'text-danger fw-bold' : ''}>{fmtPct(costTotals.margin)}</span>
                                    {costTotals.total > 0 && costTotals.costed < costTotals.total && (
                                        <span className="text-muted"> ({costTotals.costed}/{costTotals.total} costed)</span>
                                    )}
                                </div>
                            </div>
                            <div style={{ width: '90px' }}>
                                <label className="form-label small mb-0">Shipping</label>
                                <input type="number" step="0.01" className="form-control form-control-sm" value={shipping} onChange={(e) => setShipping(parseFloat(e.target.value) || 0)} />
                            </div>
                            <div style={{ width: '90px' }}>
                                <label className="form-label small mb-0">Fees</label>
                                <input type="number" step="0.01" className="form-control form-control-sm" value={additionalFees} onChange={(e) => setAdditionalFees(parseFloat(e.target.value) || 0)} />
                            </div>
                            <div style={{ width: '90px' }}>
                                <label className="form-label small mb-0">Discount</label>
                                <input type="number" step="0.01" className="form-control form-control-sm" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} />
                            </div>
                            <div style={{ width: '80px' }}>
                                <label className="form-label small mb-0">Tax</label>
                                <input type="number" step="0.01" className="form-control form-control-sm" value={tax} onChange={(e) => setTax(parseFloat(e.target.value) || 0)} />
                            </div>
                            <div style={{ width: '130px' }}>
                                <label className="form-label small mb-0 fw-bold text-success">Grand Total</label>
                                <input type="text" className="form-control form-control-sm fw-bold border-success" value={`$${grandTotal.toFixed(2)}`} readOnly style={{ backgroundColor: 'var(--jq-subtotal-bg, #e8f5e9)' }} />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <h5 className="mb-1">5. Notes</h5>
                        <textarea className="form-control form-control-sm" rows="2" value={notes} onChange={e => setNotes(e.target.value)} style={{ fontSize: '0.85rem' }}></textarea>
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                    <button className="btn btn-outline-secondary" onClick={() => navigate('/sales-orders')} disabled={saving}>
                        Cancel
                    </button>
                    <button className="btn btn-success" onClick={handleSave} disabled={saving}>
                        {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : 'Save Sales Order'}
                    </button>
                </div>
            </div>
        </div>
    );
}
